import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Chip,
    Tooltip,
    IconButton,
    Fade,
    Snackbar,
    Alert,
} from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { useNavigate, useLocation } from "react-router-dom";
import useQueueUpdates from "../../../hooks/useQueueUpdates";
import { trackEvent } from "../../../utils/analytics";
import { getQueues, updateQueueStatus, addQueue, addDirectAssistQueue, getServiceSettings, getTutorialProgress, saveTutorialProgress } from "./../../../api/api";
import { getCurrentBranchId, getCurrentUser, debugAuthData } from "../../../utils/auth";
import OnboardingTutorialOverlay from "../../../components/OnboardingTutorialOverlay";
import Logo from "../../../assests/logo.png";

// --------------------------------------------------
// Queue Card
// --------------------------------------------------
const QueueCard = ({ queue, onAssist, onDone, onLost, serviceSelectionEnabled }) => {
    const [currentTime, setCurrentTime] = useState(new Date());

    // Debug logging
    // console.log("QueueCard: Rendering queue:", queue);

    // Update current time every 10 seconds to refresh wait time display
    useEffect(() => {
        // Set initial time immediately
        setCurrentTime(new Date());

        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 10000); // Update every 10 seconds for responsive display

        return () => clearInterval(timer);
    }, []);

    const calculateWaitTime = (regTime, svcTime, status) => {
        // Handle invalid or future registration times
        if (!regTime) {
            return "0m";
        }

        // If status is SERVING and we have QueueSvcTime, use that instead of current time
        // This stops the wait time from updating after Assist is pressed
        let endTime;
        if (status === 'SERVING' && svcTime) {
            // Use QueueSvcTime - wait time is frozen at assist time
            endTime = new Date(svcTime);
        } else {
            // Use current time for WAITING queues
            const now = new Date();
            endTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Oslo"}));
        }

        // Parse registration time (stored in Norway timezone format)
        const regDate = new Date(regTime);
        
        // Check if the parsed time is valid
        if (isNaN(regDate.getTime())) {
            return "0m";
        }

        // If the registration time is in the future, return "0m"
        if (regDate > endTime) {
            return "0m";
        }

        // Calculate time difference in minutes
        const diff = Math.floor((endTime - regDate) / (1000 * 60)); // difference in minutes
        console.log("🔍 Time Difference (minutes):", diff);

        // If the difference is negative or zero, return "0m"
        if (diff <= 0) {
            return "0m";
        }

        // For very recent registrations (less than 2 minutes), show "0m" to indicate just started
        if (diff < 2) {
            return "0m";
        }

        // If the registration time is more than 24 hours ago, it's likely an old queue
        // or there's a timezone issue - treat it as a new queue
        const hoursDiff = diff / 60;
        if (hoursDiff > 24) {
            return "0m";
        }

        // If the registration time is not from today, treat it as a new queue
        const regDateStr = regDate.toLocaleDateString('no-NO', { timeZone: 'Europe/Oslo' });
        const endDateStr = endTime.toLocaleDateString('no-NO', { timeZone: 'Europe/Oslo' });
        if (regDateStr !== endDateStr) {
            return "0m";
        }

        const hours = Math.floor(diff / 60);
        const minutes = diff % 60;
        const result = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        console.log("🔍 Calculated Wait Time:", result);
        return result;
    };

    return (
        <Card
            sx={{
                textAlign: "center",
                p: 0.75,
                boxShadow: 3,
                backgroundColor:
                    queue.status === "SERVING"
                        ? "#fff9c4"
                        : queue.status === "WAITING"
                        ? "#f9f9f9"
                        : "#e8f5e8",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
            }}
        >
            <CardContent sx={{ p: "0 !important", flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.375}
                >
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", flexGrow: 1, fontSize: { xs: "2.25rem", sm: "2.75rem" } }}
                    >
                     {queue.number}
                    </Typography>

                    {queue.status === "SERVING" ? (
                        <Box sx={{ display: "flex", gap: 0.375, ml: 1, flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    backgroundColor: "red",
                                    color: "white",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    py: 0.5,
                                    px: 1,
                                    minWidth: "auto",
                                    lineHeight: 1.2,
                                }}
                                onClick={() => onLost(queue.id)}
                            >
                                Lost
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.5, px: 1, minWidth: "auto", lineHeight: 1.2 }}
                                onClick={() => onDone(queue.id)}
                            >
                                Done
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="contained"
                            size="small"
                            sx={{
                                backgroundColor: "green",
                                color: "white",
                                textTransform: "none",
                                ml: 1,
                                fontSize: "0.75rem",
                                py: 0.5,
                                px: 1,
                                minWidth: "auto",
                                lineHeight: 1.2,
                            }}
                            onClick={() => onAssist(queue.id)}
                        >
                            Assist
                        </Button>
                    )}
                </Box>

                <Box textAlign="left" mt={0.75}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Wait time updates every 10 seconds">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                            ⏱ Wait Time: {calculateWaitTime(queue.QueueRegTime, queue.QueueSvcTime, queue.status)}
                        </Typography>
                    </Tooltip>
                    </Box>
                    <style>
                        {`
                            @keyframes pulse {
                                0% { opacity: 1; }
                                50% { opacity: 0.7; }
                                100% { opacity: 1; }
                            }
                        `}
                    </style>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.125, fontSize: "0.75rem", lineHeight: 1.2 }}>
                            Services:
                        </Typography>
                        {queue.description &&
                        queue.description !== "Ticket generated via API" &&
                        queue.description !== "Ticket generated by staff" &&
                        queue.description !== "No services selected" &&
                        queue.description !== "Queue created - services pending" ? (
                            <Box component="ul" sx={{ pl: 1.5, m: 0, mb: 0, mt: 0.125 }}>
                                {queue.description
                                    .split(/[,;]\s*|\n/)
                                    .map((service, index) => {
                                        const trimmedService = service.trim();
                                        return trimmedService ? (
                                            <Typography
                                                key={index}
                                                component="li"
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 0.0625, fontSize: "0.75rem", lineHeight: 1.2 }}
                                            >
                                                {trimmedService}
                                            </Typography>
                                        ) : null;
                                    })
                                    .filter(Boolean)}
                            </Box>
                        ) : queue.description === "Queue created - services pending" && serviceSelectionEnabled ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                                Service Pending
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                                Not selected
                            </Typography>
                        )}
                    </Box>

                    {/* CRM Profile Link */}
                    {queue.crm_profile_url && (
                        <Box mt={0.375}>
                            <Button
                                variant="outlined"
                                size="small"
                                href={queue.crm_profile_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.7rem",
                                    py: 0.25,
                                    px: 0.75,
                                    minWidth: "auto",
                                    lineHeight: 1.2,
                                }}
                            >
                                🔗 CRM Profile
                            </Button>
                        </Box>
                    )}

                    {/* Customer Info */}
                    {queue.customer_name && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mt={0.375}
                            sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
                        >
                            👤 {queue.customer_name}
                        </Typography>
                    )}

                    {queue.phone_number && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                            📞 {queue.phone_number}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// --------------------------------------------------
// Add Queue Card
// --------------------------------------------------
const AddQueueCard = ({ onAdd, onDirectAssist, adding, addingDirectAssist }) => (
    <Card
        sx={{
            textAlign: "center",
            p: 0.75,
            boxShadow: 3,
            backgroundColor: "#e3f2fd",
            border: "2px dashed #2196f3",
            cursor: "pointer",
            transition: "0.3s",
            "&:hover": { backgroundColor: "#bbdefb" },
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
        }}
    >
        <CardContent sx={{ p: "0 !important" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.75, fontSize: "0.9rem" }}>
                ADD QUEUE
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onAdd}
                    disabled={adding || addingDirectAssist}
                    size="small"
                    sx={{ mb: 0.5 }}
                >
                    {adding ? (
                        <CircularProgress size={20} sx={{ color: "#2196f3" }} />
                    ) : (
                        "Add"
                    )}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={onDirectAssist}
                    disabled={adding || addingDirectAssist}
                    size="small"
                    sx={{ 
                        backgroundColor: "#4caf50",
                        "&:hover": { backgroundColor: "#45a049" }
                    }}
                >
                    {addingDirectAssist ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                        "Direct Assist"
                    )}
                </Button>
            </Box>
        </CardContent>
    </Card>
);

// --------------------------------------------------
// Empty State Card
// --------------------------------------------------
// --------------------------------------------------
// Empty State Card
// --------------------------------------------------
const EmptyState = ({ onAdd, onDirectAssist, adding, addingDirectAssist }) => (
    <Card
        sx={{
            textAlign: "center",
            p: 3,
            boxShadow: 3,
            backgroundColor: "#f9f9f9",
        }}
    >
        <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
                NO CUSTOMERS WAITING
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onAdd}
                    disabled={adding || addingDirectAssist}
                    data-onboarding-id="onboarding-add-queue"
                >
                    {adding ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                        "Add Queue"
                    )}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={onDirectAssist}
                    disabled={adding || addingDirectAssist}
                    data-onboarding-id="onboarding-direct-assist"
                    sx={{
                        backgroundColor: "#4caf50",
                        "&:hover": { backgroundColor: "#45a049" }
                    }}
                >
                    {addingDirectAssist ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                        "Direct Assist"
                    )}
                </Button>
            </Box>
        </CardContent>
    </Card>
);

// --------------------------------------------------
// Queue List Wrapper
// --------------------------------------------------
const QueueList = ({ queues, onAssist, onDone, onLost, onAdd, onDirectAssist, adding, addingDirectAssist, serviceSelectionEnabled }) => {
    if (!queues.length) {
        return (
            <Grid item xs={12}>
                <EmptyState 
                    onAdd={onAdd} 
                    onDirectAssist={onDirectAssist}
                    adding={adding} 
                    addingDirectAssist={addingDirectAssist}
                />
            </Grid>
        );
    }

    return (
        <>
            {queues.map((queue) => (
                <Grid item xs={6} sm={2} md={2} key={queue.id} sx={{ mb: 1 }}>
                    <QueueCard
                        queue={queue}
                        onAssist={onAssist}
                        onDone={onDone}
                        onLost={onLost}
                        serviceSelectionEnabled={serviceSelectionEnabled}
                    />
                </Grid>
            ))}
            <Grid item xs={12} sm={2} md={2} sx={{ mb: 1 }}>
                <AddQueueCard 
                    onAdd={onAdd} 
                    onDirectAssist={onDirectAssist}
                    adding={adding} 
                    addingDirectAssist={addingDirectAssist}
                />
            </Grid>
        </>
    );
};

// --------------------------------------------------
// Main Staff Component
// --------------------------------------------------
const Staff = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [queues, setQueues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingQueue, setAddingQueue] = useState(false);
    const [addingDirectAssist, setAddingDirectAssist] = useState(false);
    const [newQueueId, setNewQueueId] = useState(null);
    const [directAssistFlash, setDirectAssistFlash] = useState(null); // queue number to flash
    const [quotaAlert, setQuotaAlert] = useState(""); // plan ticket-limit message
    const [serviceSelectionEnabled, setServiceSelectionEnabled] = useState(true);
    const [runOnboardingTutorial, setRunOnboardingTutorial] = useState(false);
    const [tutorialProgress, setTutorialProgress] = useState(null);
    const addQueueInFlight = useRef(false);
    const directAssistInFlight = useRef(false);
    // Synchronous time-based debounce. The ref-based in-flight guard below
    // already blocks concurrent clicks, but a belt-and-suspenders time window
    // protects against edge cases (slow network masking the spinner, fast
    // double-taps on touch devices, the fallback timeout resetting the ref
    // while a request is still in flight). Kept outside state so reads/writes
    // are fully synchronous — no render delay between click and guard update.
    const lastAddClickAt = useRef(0);
    const ADD_DEBOUNCE_MS = 1500;

    const branchId = getCurrentBranchId();
    const currentUser = getCurrentUser();
    const branchName = currentUser?.branch_name || "Unknown Branch";
    
    // Call debug function to show all auth data
    debugAuthData();

    // Mark that user has visited the live queue page
    useEffect(() => {
        localStorage.setItem('live_queue_visited', 'true');
    }, []);

    useEffect(() => {
        if (!currentUser?.is_free_trial) return;
        getTutorialProgress(branchId)
            .then((res) => {
                if (res?.success && res?.progress) setTutorialProgress(res.progress);
            })
            .catch(() => {});
    }, [branchId, currentUser?.is_free_trial]);

    useEffect(() => {
        const fromReplay = location.state?.startOnboardingTour === true;
        if (fromReplay) {
            setRunOnboardingTutorial(true);
            return;
        }
        if (loading || !tutorialProgress || !currentUser?.is_free_trial) return;
        if (tutorialProgress?.onboardingPhases?.qr !== true) return;
        if (tutorialProgress?.onboardingPhases?.live_queue === true) return;
        setRunOnboardingTutorial(true);
    }, [loading, tutorialProgress, currentUser?.is_free_trial, location.state?.startOnboardingTour]);

    const handleOnboardingPhaseComplete = async (page) => {
        setRunOnboardingTutorial(false);
        try {
            const res = await getTutorialProgress(branchId);
            const progress = res?.success && res?.progress ? { ...res.progress } : {};
            progress.onboardingPhases = progress.onboardingPhases || {};
            progress.onboardingPhases[page] = true;
            await saveTutorialProgress(branchId, progress);
            if (page === "live_queue") {
                navigate("/home/panel", { replace: true, state: { startOnboardingTour: "dashboard_final" } });
            }
        } catch (e) {
            console.error("Failed to save onboarding phase:", e);
            if (page === "live_queue") {
                navigate("/home/panel", { replace: true, state: { startOnboardingTour: "dashboard_final" } });
            }
        }
    };

    const handleAddQueue = async () => {
        // Hardened click guard (synchronous, runs before any await).
        // Record EVERY click attempt — accepted or rejected — so that a user
        // mashing the button during a slow API keeps extending the lockout
        // window. Without this, a spam click that lands right after the
        // previous queue becomes visible would pass the debounce and create
        // a duplicate.
        const nowTs = Date.now();
        const prevClickAt = lastAddClickAt.current;
        lastAddClickAt.current = nowTs;

        // Debounce: reject if another click happened within the window.
        // prevClickAt === 0 means this is the very first click — let it through.
        if (prevClickAt > 0 && nowTs - prevClickAt < ADD_DEBOUNCE_MS) {
            return;
        }
        // In-flight ref guard: reject if a request is already outstanding.
        if (addQueueInFlight.current || directAssistInFlight.current) {
            return;
        }
        addQueueInFlight.current = true;
        try {
            setAddingQueue(true); // Set loading state immediately

            // Get current time in Norway timezone (Europe/Oslo) to match backend storage
            const now = new Date();
            const norwayTime = new Intl.DateTimeFormat('sv-SE', {
                timeZone: 'Europe/Oslo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }).format(now).replace('T', ' ').replace('Z', '');

            console.log("PC Time Details:", {
                UTC: now.toISOString(),
                Norway: norwayTime,
                Local: now.toLocaleString(),
                Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });

            const newQueue = await addQueue(branchId, norwayTime);
            console.log("✅ Queue created successfully:", newQueue);

            if (newQueue) {
                // Optimistically render the card from the POST response, which
                // already contains the new id + queue number. This avoids a
                // second remote round-trip (GET /queues) before the card shows,
                // so it appears the instant the create returns. fetchQueues()
                // below reconciles in the background (matched by id, so no
                // duplicate) and the Pusher broadcast updates other clients.
                const optimisticQueue = {
                    id: newQueue.id,
                    number: newQueue.queue_number,
                    status: "WAITING",
                    QueueRegTime: norwayTime,
                    QueueSvcTime: null,
                    description: "Ticket generated by staff",
                };
                setQueues((prev) =>
                    prev.some((q) => q.id === optimisticQueue.id)
                        ? prev
                        : [...prev, optimisticQueue]
                );
                setNewQueueId(newQueue.id);
                // Reconcile with the server in the background — do NOT await, so
                // the card is never blocked on the GET round-trip.
                fetchQueues();
                // Reset loading state after successful creation
                setTimeout(() => {
                    setAddingQueue(false);
                    setNewQueueId(null);
                    addQueueInFlight.current = false;
                }, 1000); // Give time for the queue to appear in the list
            } else {
                setAddingQueue(false);
                addQueueInFlight.current = false;
            }
        } catch (error) {
            console.error("❌ Error adding queue:", error);
            if (error.response?.status === 403 && error.response?.data?.quota_reached) {
                setQuotaAlert(error.response.data.error || "You have reached your plan's monthly ticket limit.");
            }
            setAddingQueue(false); // Reset loading state on error
            addQueueInFlight.current = false;
        }
    };

    const handleAddDirectAssist = async () => {
        // Same hardened click guard as handleAddQueue — every click attempt
        // is recorded so rapid clicks keep the lockout window rolling.
        // Shares the same timestamp ref with Add Queue so the debounce
        // covers rapid toggling between the two buttons.
        const nowTs = Date.now();
        const prevClickAt = lastAddClickAt.current;
        lastAddClickAt.current = nowTs;

        if (prevClickAt > 0 && nowTs - prevClickAt < ADD_DEBOUNCE_MS) {
            return;
        }
        if (directAssistInFlight.current || addQueueInFlight.current) {
            return;
        }
        directAssistInFlight.current = true;
        try {
            setAddingDirectAssist(true); // Set loading state immediately

            // Get current time in Norway timezone (Europe/Oslo) to match backend storage
            const now = new Date();
            const norwayTime = new Intl.DateTimeFormat('sv-SE', {
                timeZone: 'Europe/Oslo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }).format(now).replace('T', ' ').replace('Z', '');

            console.log("Direct Assist - PC Time Details:", {
                UTC: now.toISOString(),
                Norway: norwayTime,
                Local: now.toLocaleString(),
                Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });

            const newQueue = await addDirectAssistQueue(branchId, norwayTime);
            console.log("✅ Direct assist queue created successfully:", newQueue);

            if (newQueue) {
                setNewQueueId(newQueue.id);
                setDirectAssistFlash(newQueue.queue_number);
                setAddingDirectAssist(false);
                // Hide flash card after 2.5 seconds
                setTimeout(() => {
                    setDirectAssistFlash(null);
                    setNewQueueId(null);
                    directAssistInFlight.current = false;
                }, 2500);
            } else {
                setAddingDirectAssist(false);
                directAssistInFlight.current = false;
            }
        } catch (error) {
            console.error("❌ Error adding direct assist queue:", error);
            if (error.response?.status === 403 && error.response?.data?.quota_reached) {
                setQuotaAlert(error.response.data.error || "You have reached your plan's monthly ticket limit.");
            }
            setAddingDirectAssist(false); // Reset loading state on error
            directAssistInFlight.current = false;
        }
    };

    const fetchQueues = useCallback(async () => {
        try {
            console.log("🔍 Fetching queues for branchId:", branchId);
            const data = await getQueues(branchId);
            // API always returns { queues: [...], last_queue_number, last_queue_status }
            const queuesList = Array.isArray(data) ? data : (data?.queues ?? []);
            console.log("📊 Received queue data:", queuesList);
            console.log("📊 Queue count:", queuesList.length);
            setQueues(queuesList);
        } catch (error) {
            console.error("❌ Error fetching queues:", error);
        } finally {
            setLoading(false);
        }
    }, [branchId]);

    const handleAssist = useCallback(
        async (id) => {
            try {
                await updateQueueStatus(id, branchId, "SERVING");
                trackEvent("queue_assist");
                setQueues((prev) =>
                    prev.map((q) =>
                        q.id === id
                            ? { ...q, status: "SERVING", isAssisting: true }
                            : q
                    )
                );
            } catch (error) {
                console.error("❌ Staff: Error marking assist:", error);
            }
        },
        [branchId]
    );

    const handleDone = useCallback(
        async (id) => {
            try {
                await updateQueueStatus(id, branchId, "ASSISTED");
                trackEvent("queue_done");
                setQueues((prev) => prev.filter((q) => q.id !== id));
            } catch (error) {
                console.error("❌ Staff: Error finishing assist:", error);
            }
        },
        [branchId]
    );

    const handleLost = useCallback(
        async (id) => {
            try {
                await updateQueueStatus(id, branchId, "LOST");
                trackEvent("queue_lost");
                setQueues((prev) => prev.filter((q) => q.id !== id));
            } catch (error) {
                console.error("❌ Staff: Error marking queue as lost:", error);
            }
        },
        [branchId]
    );

    // Fetch service selection status
    useEffect(() => {
        const fetchServiceSettings = async () => {
            try {
                const settings = await getServiceSettings(branchId);
                if (settings.success) {
                    setServiceSelectionEnabled(settings.data?.service_selection_enabled ?? true);
                }
            } catch (error) {
                console.error("Error fetching service settings:", error);
                // Default to true if there's an error
                setServiceSelectionEnabled(true);
            }
        };
        fetchServiceSettings();
    }, [branchId]);

    useEffect(() => {
        fetchQueues();
    }, [fetchQueues]);

    // Live queue updates. The 5-minute auto-expire of SERVING queues is swept
    // server-side every minute by `queues:auto-expire`, which broadcasts its
    // own `auto_expired` events - so an idle branch no longer needs a poll to
    // notice. The hook still polls whenever the socket is down.
    useQueueUpdates({
        branchId,
        onRefresh: fetchQueues,
        onQueueEvent: (queueData, action) => {
            const queueId = queueData.QueueID || queueData.id;
            const queueStatus = queueData.QueueStatus || queueData.status;

            // Auto-expired, lost or assisted queues leave the panel straight
            // away; anything else needs the full list to stay in order.
            const isFinished =
                action === "auto_expired" ||
                action === "auto_expire" ||
                queueStatus === "LOST" ||
                queueStatus === "ASSISTED";

            if (!isFinished) {
                fetchQueues();
                return;
            }

            setQueues((prev) =>
                prev.filter((q) => (q.id || q.QueueID) !== queueId)
            );
        },
    });

    useEffect(() => {
        if (newQueueId && Array.isArray(queues) && queues.find((q) => q.id === newQueueId)) {
            console.log("✅ New queue detected in list, resetting loading state");
            setAddingQueue(false);
            setNewQueueId(null);
            // Release the in-flight ref the moment the queue is visible in the
            // list, so the button state and ref guard stay in sync. The 1s
            // setTimeout in handleAddQueue is a backup and will no-op.
            addQueueInFlight.current = false;
        }
    }, [queues, newQueueId]);

    // Fallback: Reset loading state if it's been stuck for too long.
    // Bumped to 15s — 5s was too aggressive on slow networks and could reset
    // the ref guard while the request was still in flight, letting a second
    // click create a duplicate queue.
    useEffect(() => {
        if (addingQueue) {
            const timeout = setTimeout(() => {
                console.log("⚠️ Adding queue timeout - resetting loading state");
                setAddingQueue(false);
                setNewQueueId(null);
                addQueueInFlight.current = false;
            }, 15000);

            return () => clearTimeout(timeout);
        }
    }, [addingQueue]);

    // Fallback: Reset loading state for direct assist if it's been stuck for too long.
    // Same 15s bump as Add Queue, same rationale.
    useEffect(() => {
        if (addingDirectAssist) {
            const timeout = setTimeout(() => {
                console.log("⚠️ Adding direct assist queue timeout - resetting loading state");
                setAddingDirectAssist(false);
                setNewQueueId(null);
                directAssistInFlight.current = false;
            }, 15000);

            return () => clearTimeout(timeout);
        }
    }, [addingDirectAssist]);

    // Fix scrolling issue on mobile
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        // Save original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalBodyHeight = bodyElement.style.height;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalHtmlHeight = htmlElement.style.height;
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;
        
        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add CSS class
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        return () => {
            // Restore original styles
            bodyElement.style.overflow = originalBodyOverflow;
            bodyElement.style.height = originalBodyHeight;
            htmlElement.style.overflow = originalHtmlOverflow;
            htmlElement.style.height = originalHtmlHeight;
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow;
                rootElement.style.height = originalRootHeight;
            }
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 3 }}
            >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <img
                        src={Logo}
                        alt="Logo"
                        style={{
                            height: "32px",
                            marginRight: "16px",
                        }}
                    />
                    <Button variant="contained" sx={{ mb: { xs: 2, sm: 0 } }}>
                        LOCATION: {branchName.toUpperCase()}
                    </Button>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                        onClick={() => setRunOnboardingTutorial(true)}
                        title="Show Tutorial"
                        color="primary"
                    >
                        <HelpIcon />
                    </IconButton>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate("/home")}
                    >
                        ADMIN
                    </Button>
                </Box>
            </Box>

            {/* Content */}
            {loading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={1} alignItems="stretch" data-onboarding-id="onboarding-live-queue-panel">
                    <QueueList
                        queues={queues}
                        onAssist={handleAssist}
                        onDone={handleDone}
                        onLost={handleLost}
                        onAdd={handleAddQueue}
                        onDirectAssist={handleAddDirectAssist}
                        adding={addingQueue}
                        addingDirectAssist={addingDirectAssist}
                        serviceSelectionEnabled={serviceSelectionEnabled}
                    />
                </Grid>
            )}
            <OnboardingTutorialOverlay
                page="live_queue"
                run={runOnboardingTutorial}
                onPhaseComplete={handleOnboardingPhaseComplete}
                onClose={() => setRunOnboardingTutorial(false)}
            />

            {/* Direct Assist Queue Flash Card */}
            <Fade in={directAssistFlash !== null} timeout={{ enter: 300, exit: 500 }}>
                <Box
                    sx={{
                        position: "fixed",
                        top: 80,
                        left: 16,
                        zIndex: 9999,
                        pointerEvents: "none",
                    }}
                >
                    <Card
                        elevation={8}
                        sx={{
                            bgcolor: "#4caf50",
                            color: "#fff",
                            borderRadius: 3,
                            px: 5,
                            py: 2.5,
                            textAlign: "center",
                            minWidth: 200,
                        }}
                    >
                        <Typography variant="body2" sx={{ opacity: 0.9, letterSpacing: 1, textTransform: "uppercase", mb: 0.5 }}>
                            Queue Created
                        </Typography>
                        <Typography variant="h2" fontWeight="bold" sx={{ lineHeight: 1 }}>
                            {directAssistFlash}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                            Direct Assist
                        </Typography>
                    </Card>
                </Box>
            </Fade>

            {/* Plan ticket-limit notification */}
            <Snackbar
                open={Boolean(quotaAlert)}
                autoHideDuration={8000}
                onClose={() => setQuotaAlert("")}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity="warning"
                    variant="filled"
                    onClose={() => setQuotaAlert("")}
                    sx={{ width: "100%" }}
                >
                    {quotaAlert}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Staff;
